import React, { useState, useEffect, useRef } from 'react';
import { Star, Send, Camera, X, ImageIcon } from 'lucide-react';
import { apiClient, publicApi, formatApiError } from '../context/AuthContext';
import { useTurnstile } from '../components/Turnstile';
import toast from 'react-hot-toast';
import { ReviewPhotos } from '../components/PhotoLightbox';

function Stars({ rating, interactive, onRate }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          type={interactive ? 'button' : undefined}
          onClick={interactive ? () => onRate(i) : undefined}
          className={interactive ? 'cursor-pointer' : 'cursor-default'}
        >
          <Star size={interactive ? 24 : 16} className={i <= rating ? "text-[#00aaff]" : "text-gray-200"} fill={i <= rating ? "#00aaff" : "#e5e7eb"} />
        </button>
      ))}
    </div>
  );
}

const MAX_PHOTOS = 5;

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    author_name: '',
    author_email: '',
    rating: 5,
    comment: '',
    product_name: ''
  });
  const [photos, setPhotos] = useState([]); // array of uploaded URLs
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const { getToken } = useTurnstile();

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const { data } = await apiClient.get('/reviews');
        setReviews(data);
      } catch (err) {
        console.error('Error fetching reviews:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      toast.error(`${MAX_PHOTOS} photos maximum`);
      e.target.value = '';
      return;
    }
    const toUpload = files.slice(0, remaining);
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of toUpload) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await publicApi.post('/upload/review-photo', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data?.url) uploaded.push(data.url);
      }
      setPhotos(prev => [...prev, ...uploaded]);
      if (uploaded.length) toast.success(`${uploaded.length} photo(s) ajoutée(s)`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removePhoto = (idx) => {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const ttoken = await getToken();
      await apiClient.post('/reviews', { ...form, photos }, {
        headers: ttoken ? { 'X-Turnstile-Token': ttoken } : {}
      });
      toast.success('Merci ! Votre avis sera publié après validation.');
      setShowForm(false);
      setForm({ author_name: '', author_email: '', rating: 5, comment: '', product_name: '' });
      setPhotos([]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f7fa]" data-testid="reviews-page">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
        <div className="text-center mb-10">
          <p className="text-xs text-[#00aaff] uppercase tracking-widest font-semibold mb-2">Témoignages</p>
          <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">Avis de nos clients</h1>
          <div className="flex items-center justify-center gap-2">
            <Stars rating={5} />
            <span className="text-gray-500 font-medium">4.9/5 • +8 743 clients</span>
          </div>
        </div>

        <div className="flex justify-center mb-8">
          <button
            onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center gap-2 bg-[#00aaff] text-white px-6 py-3 rounded-full font-bold hover:bg-[#0088dd] transition-colors"
            data-testid="toggle-review-form"
          >
            <Send size={16} /> Laisser un avis
          </button>
        </div>

        {showForm && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-8" data-testid="review-form">
            <h3 className="font-bold text-gray-900 mb-4">Partagez votre expérience</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Votre nom *"
                  value={form.author_name}
                  onChange={e => setForm({ ...form, author_name: e.target.value })}
                  required
                  className="border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                  data-testid="review-author-name"
                />
                <input
                  type="email"
                  placeholder="Votre email *"
                  value={form.author_email}
                  onChange={e => setForm({ ...form, author_email: e.target.value })}
                  required
                  className="border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                  data-testid="review-author-email"
                />
              </div>
              <input
                type="text"
                placeholder="Produit acheté (optionnel)"
                value={form.product_name}
                onChange={e => setForm({ ...form, product_name: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                data-testid="review-product-name"
              />
              <div>
                <p className="text-sm text-gray-500 mb-2">Votre note</p>
                <Stars rating={form.rating} interactive onRate={(r) => setForm({ ...form, rating: r })} />
              </div>
              <textarea
                placeholder="Votre avis *"
                value={form.comment}
                onChange={e => setForm({ ...form, comment: e.target.value })}
                required
                rows={4}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] resize-none"
                data-testid="review-comment"
              />

              {/* Photo uploader */}
              <div>
                <p className="text-sm text-gray-500 mb-2 flex items-center gap-2">
                  <ImageIcon size={15} />
                  Ajouter des photos <span className="text-gray-400">— {photos.length}/{MAX_PHOTOS} (optionnel)</span>
                </p>
                <div className="flex flex-wrap gap-3">
                  {photos.map((url, idx) => (
                    <div key={idx} className="relative group w-20 h-20 rounded-xl overflow-hidden ring-1 ring-gray-200">
                      <img src={url} alt={`photo ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Supprimer la photo"
                        data-testid={`remove-review-photo-${idx}`}
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                  {photos.length < MAX_PHOTOS && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 hover:border-[#00aaff] hover:bg-[#00aaff]/5 text-gray-400 hover:text-[#00aaff] flex flex-col items-center justify-center transition-colors disabled:opacity-60"
                      data-testid="add-review-photo-btn"
                    >
                      {uploading ? (
                        <div className="w-5 h-5 border-2 border-[#00aaff] border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Camera size={20} />
                          <span className="text-[10px] font-medium mt-1">Ajouter</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  multiple
                  onChange={handleFilesSelected}
                  className="hidden"
                />
                <p className="text-[11px] text-gray-400 mt-2">JPG, PNG, WEBP · 5 Mo max par photo</p>
              </div>

              <button
                type="submit"
                disabled={submitting || uploading}
                className="w-full bg-[#00aaff] text-white py-3 rounded-full font-bold hover:bg-[#0088dd] transition-colors disabled:opacity-60"
                data-testid="submit-review"
              >
                {submitting ? 'Envoi...' : 'Envoyer mon avis'}
              </button>
            </form>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#00aaff] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
            <Star size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">Aucun avis pour l'instant</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {reviews.map(review => {
              const reviewPhotos = Array.isArray(review.photos) ? review.photos.filter(Boolean) : [];
              return (
                <div key={review.id} className="bg-white border border-gray-100 rounded-2xl p-6" data-testid={`review-card-${review.id}`}>
                  <Stars rating={review.rating} />
                  <p className="text-gray-600 mt-3 leading-relaxed italic">"{review.comment}"</p>
                  {reviewPhotos.length > 0 && (
                    <div className="mt-4">
                      <ReviewPhotos photos={reviewPhotos} size="md" maxThumbs={4} />
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-4">
                    <div className="w-8 h-8 rounded-full bg-[#e0f4ff] flex items-center justify-center text-[#00aaff] font-bold text-sm">
                      {review.author_name?.[0] || "?"}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{review.author_name}</p>
                      {review.product_name && <p className="text-xs text-gray-400">{review.product_name}</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
